<script setup>
import ModalWrapper from '@/modals/ModalWrapper';
import { ref } from 'vue';
import { useToast } from 'vue-toastification';
import { useMemberProfileStore } from '@/stores/memberProfile';
import { trans } from 'laravel-vue-i18n';
import { useOptionsStore } from '@/stores/options';
import { storeToRefs } from 'pinia';

const optionsStore = useOptionsStore();
const {
    organizationsOptions,
    sportsOptions,
    assessmentOptions,
    assessmentDomainOptions,
} = storeToRefs(optionsStore);

const props = defineProps({
    profile: {
        type: Object,
        default: () => ({})
    },
    mode: {
        type: String,
        default: 'Create'
    }
});

const emit = defineEmits(['close-modal']);
const toast = useToast();
const { store, update } = useMemberProfileStore();

const assessmentOptionsForDomain = (domainId) => {
    return assessmentOptions.value.filter((assessment) => assessment.domain_id === domainId);
};

const selectedAssessmentsByDomain = () => {
    return Object.fromEntries(assessmentDomainOptions.value.map((domain) => {
        const assignedAssessment = props.profile?.member_assessments?.find((memberAssessment) => {
            const domainId = memberAssessment.assessment_domain_id ?? memberAssessment.assessment?.domain_id;

            return domainId === domain.value;
        });

        const defaultAssessment = assessmentOptionsForDomain(domain.value)[0];

        return [
            domain.value,
            assignedAssessment?.assessment_id ?? (props.mode === 'Create' ? defaultAssessment?.value ?? null : null),
        ];
    }));
};

const form = ref({
    organization_id: props.profile?.organization?.id || null,
    sport_id: props.profile?.sport_id || null,
    assessments_by_domain: selectedAssessmentsByDomain(),
});

const save = async () => {
    const action = props.mode === 'Edit' ? update : store;
    const assessmentIds = Object.values(form.value.assessments_by_domain).filter(Boolean);

    try {
        await action({
            organization_id: form.value.organization_id,
            sport_id: form.value.sport_id,
            assessment_ids: assessmentIds,
            hash: props.profile?.hash || null,
        });
        toast.success(trans(
            props.mode === 'Edit' ? 'actions.update.success' : 'actions.create.success',
            { type: trans('label.member_profile') }
        ));
        emit('close-modal');
    } catch (error) {
        toast.error(error?.response?.data?.message || trans('actions.update.error', { type: trans('label.member_profile') }));
    }
};
</script>

<template>
<ModalWrapper>
    <template #header>{{ $t('label.mode_what', { 'mode' : mode, 'what' : $t('label.member_profile') }) }}</template>
    <template #subheader>{{ $t('label.create_or_edit', { 'what' : $t('label.member_profile') }) }}</template>
    <form @submit.prevent="save">
        <div class="grid md:grid-cols-2 gap-x-4 gap-y-3">
            <StyledDropdown 
                v-model="form.organization_id"
                name="organization"
                :options="organizationsOptions"
                :label="$t('label.organization')" 
                :clearable="false" />
            <StyledDropdown
                v-model="form.sport_id"
                name="sport_id"
                :options="sportsOptions"
                :label="$t('label.sport')"
                :clearable="false" />

            <div class="md:col-span-2 space-y-3">
                <p class="text-sm font-medium text-gray-700">
                    {{ $t('label.assigned_assessments') }}
                </p>

                <div class="grid md:grid-cols-2 gap-x-4 gap-y-3">
                    <v-autocomplete
                        v-for="domain in assessmentDomainOptions"
                        :key="domain.value"
                        v-model="form.assessments_by_domain[domain.value]"
                        :items="assessmentOptionsForDomain(domain.value)"
                        item-title="label"
                        item-value="value"
                        :label="domain.label"
                        clearable
                        variant="outlined"
                        density="comfortable" />
                </div>
            </div>
        </div>
    </form>
    <template #footer>
        <StyledSaveButton modal @save="save" />
    </template>
</ModalWrapper>
</template>
